import { BROKER_SYNC_RUN_STATUS_OBJ, isCalendarDate, PRICE_SOURCE_OBJ, PORTFOLIO_VALUATION_STATUS_OBJ,
  PORTFOLIO_INSTRUMENT_KIND_OBJ, PORTFOLIO_INSTRUMENT_PRICE_SOURCE_OBJ } from "@signals/shared";
import type { BrokerSyncAccountStatus, BrokerSyncAccountsResponse } from "./broker-sync-panel.types";
import type { Account, PortfolioSummary, Position } from "./portfolio.types";

const dateFormatter = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const shortDateFormatter = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short", timeZone: "UTC" });
const checkFormatter = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "shortOffset" });

export function successfulBrokerCheckAt(account: BrokerSyncAccountStatus): string | null {
  const value = account.lastSuccessfulRun?.finishedAt
    ?? (account.latestRun?.status === BROKER_SYNC_RUN_STATUS_OBJ.SUCCEEDED ? account.latestRun.finishedAt : null);
  return value && Number.isFinite(Date.parse(value)) ? value : null;
}

export function formatBrokerCheck(value: string | null): string {
  return value && Number.isFinite(Date.parse(value)) ? checkFormatter.format(new Date(value)) : "Неизвестно";
}

export function formatPortfolioDate(value: string): string {
  return isCalendarDate(value) ? dateFormatter.format(new Date(value + "T12:00:00Z")) : "Дата неизвестна";
}

function formatPriceDateRange(first: string, last: string): string {
  if (first === last) return formatPortfolioDate(first);
  if (first.slice(0, 7) === last.slice(0, 7)) return String(Number(first.slice(8))) + "–" + formatPortfolioDate(last);
  const start = first.slice(0, 4) === last.slice(0, 4)
    ? shortDateFormatter.format(new Date(first + "T12:00:00Z")) : formatPortfolioDate(first);
  return start + " — " + formatPortfolioDate(last);
}

/** A zero option mark needs the same dated account/contract observation as the accepted valuation. */
function confirmedZeroOptionMark(position: Position): boolean {
  const instrument = position.instrument;
  const evidence = position.valuationEvidence;
  const price = position.financialAvailability?.inputs.marketPrice;
  return position.currentPrice === 0 && instrument?.kind === PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION &&
    (!position.optionContractId || position.optionContractId === instrument.optionContractId) &&
    position.marketPriceSource === PORTFOLIO_INSTRUMENT_PRICE_SOURCE_OBJ.IBKR &&
    evidence?.source === PORTFOLIO_INSTRUMENT_PRICE_SOURCE_OBJ.IBKR && evidence.accountId === position.accountId &&
    evidence.instrument.kind === PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION && evidence.instrument.optionContractId === instrument.optionContractId &&
    evidence.priceDate === position.marketPriceDate && Boolean(evidence.sourceVersion) && evidence.availability.available && price?.available === true &&
    position.financialAvailability?.inputs.holdings.available === true &&
    evidence.evidenceIds.some((id) => price.evidenceIds.includes(id)) &&
    price.coverage.positions.included.some((ref) => ref.accountId === position.accountId && ref.kind === PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION && ref.optionContractId === instrument.optionContractId);
}

/** These are dates of usable prices in this exact valuation, never a browser fetch timestamp. */
export function portfolioPriceFreshness(summary: PortfolioSummary | null, cutoff: string) {
  const incomplete = summary?.valuationStatus === PORTFOLIO_VALUATION_STATUS_OBJ.INCOMPLETE || summary?.totalValueUSD === null;
  const positions = summary?.positions;
  if (!positions) return { label: "Цены: даты неизвестны", quality: incomplete ? "Оценка неполная" : "Даты цен не получены", missingDateCount: null };
  const held = positions.filter(position => position.quantity !== 0);
  if (held.length === 0) return { label: "Цены: не требуются", quality: incomplete ? "Оценка неполная" : "Нет открытых позиций в ценных бумагах", missingDateCount: 0 };
  const dates = held.flatMap(position => {
    const date = position.marketPriceDate;
    return position.quantity != null && Number.isFinite(position.quantity) && position.marketValue != null && position.currentPrice != null && (position.currentPrice > 0 || confirmedZeroOptionMark(position))
      && date && isCalendarDate(date) && (!isCalendarDate(cutoff) || date <= cutoff) ? [date] : [];
  }).sort();
  const first = dates.at(0), last = dates.at(-1);
  const missingDateCount = held.length - dates.length;
  const estimated = summary?.isEstimated || held.some(position => position.marketPriceIsStale || position.marketPriceSource === PRICE_SOURCE_OBJ.MANUAL);
  return {
    label: first && last ? "Цены: " + formatPriceDateRange(first, last) + (missingDateCount ? " · не у всех позиций" : "") : "Цены: нет подтверждённых дат",
    quality: incomplete ? "Оценка неполная" : estimated ? "Есть старые или ручные цены" : missingDateCount ? "Не все даты цен известны" : "Даты использованных цен известны",
    missingDateCount,
  };
}

export function portfolioBrokerFreshness(data: BrokerSyncAccountsResponse | undefined, accounts: readonly Account[] | undefined) {
  const current = (data?.items ?? []).filter(account => !account.closedOn);
  const checks = current.flatMap(account => { const value = successfulBrokerCheckAt(account); return value ? [value] : []; });
  const oldestSuccessfulAt = checks.length > 0 && checks.length === current.length
    ? checks.sort((left, right) => Date.parse(left) - Date.parse(right)).at(0) ?? null : null;
  const running = current.some(account => account.latestRun?.status === BROKER_SYNC_RUN_STATUS_OBJ.RUNNING);
  const failed = current.some(account => account.latestRun?.status === BROKER_SYNC_RUN_STATUS_OBJ.FAILED);
  const issueCount = data ? data.items.reduce((sum, account) => sum + account.openIssueCount, 0) : null;
  const manualAccountCount = accounts
    ? accounts.filter(account => !account.closedOn && !current.some(linked => linked.accountId === account.id)).length : null;
  return { current, oldestSuccessfulAt, running, failed, issueCount, manualAccountCount };
}
